import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowRight, Loader2, X, Sparkles, Calendar } from "lucide-react";

const TypewriterText = ({ text, onComplete, delay = 30 }) => {
  const [displayedText, setDisplayedText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, delay);
      return () => clearTimeout(timeout);
    } else if (onComplete) {
      onComplete();
    }
  }, [currentIndex, text, delay, onComplete]);

  return <span>{displayedText}</span>;
};

const OnboardingModal = ({ isOpen, onClose, onComplete }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // Start at step 0 (welcome screen)
  const [typing, setTyping] = useState(true);
  const [formData, setFormData] = useState({
    revenueGoal: "",
    goalEndDate: "",
    companyName: "",
    products: "",
    icp: "",
    website: "",
    targetAccounts: "",
    currentCustomers: ""
  });
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  // Debug: Log whenever isOpen changes
  useEffect(() => {
    console.log(`[OnboardingModal] isOpen prop value: ${isOpen}`);
  }, [isOpen]);

  const questions = {
    0: "Welcome to Intelligent Sales Machines. Your autonomous AI sales agent that works 24/7 to help you hit your revenue goals.",
    1: "Hello, tell me what your revenue goal is...I will be your super intelligence to help you reach this goal.",
    2: "By what date do you want to accomplish this goal? This helps me prioritize your sales activities.",
    3: "What's your company name and website? Also, tell me about the products or services you sell.",
    4: "Who are your target accounts? List a few companies you'd like to win as customers.",
    5: "Who are your current customers? This helps me understand what's working and find similar prospects.",
    6: "Lastly, what is your Ideal Customer Profile and target market? If not known, I can help determine this."
  };

  const handleWelcomeNext = () => {
    setTyping(true);
    setStep(1);
  };

  const handleStep1Next = () => {
    const goal = parseFloat(formData.revenueGoal);
    if (!formData.revenueGoal || isNaN(goal) || goal < 1000) {
      toast.error("Please enter a revenue goal of at least $1,000");
      return;
    }
    setTyping(true);
    setStep(2);
  };

  const handleStep2Next = () => {
    if (!formData.goalEndDate) {
      toast.error("Please select a target date for your goal");
      return;
    }
    
    // Validate date is in the future
    const selectedDate = new Date(formData.goalEndDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (selectedDate <= today) {
      toast.error("Please select a future date");
      return;
    }
    
    setTyping(true);
    setStep(3);
  };

  const handleStep3Next = () => {
    if (!formData.companyName || formData.companyName.trim().length < 2) {
      toast.error("Please provide your company name");
      return;
    }
    if (!formData.products || formData.products.trim().length < 10) {
      toast.error("Please provide details about your products or services");
      return;
    }
    
    setTyping(true);
    setStep(4);
  };

  const handleStep4Next = () => {
    // Target accounts are optional but encouraged
    setTyping(true);
    setStep(5);
  };

  const handleStep5Next = () => {
    // Current customers are optional but encouraged
    setTyping(true);
    setStep(6);
  };

  const handleSkipICP = () => {
    setFormData(prev => ({ ...prev, icp: "Please help me determine my ideal customer profile based on my products and services." }));
    handleCompleteSetup();
  };

  const handleCompleteSetup = async () => {
    if (!formData.icp || formData.icp.trim().length < 5) {
      toast.error("Please provide your ICP or choose to skip");
      return;
    }

    setLoading(true);
    setProgress(0);

    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 95) {
          clearInterval(progressInterval);
          return 95;
        }
        return prev + 5;
      });
    }, 150);

    try {
      // Get existing userId from localStorage (from registration)
      const existingUserId = localStorage.getItem("userId");
      
      // Build the URL with userId if we have one
      const url = existingUserId 
        ? `${API}/users?userId=${existingUserId}`
        : `${API}/users`;
      
      const response = await axios.post(url, {
        companyName: formData.companyName || "My Company",
        products: formData.products,
        revenueGoal: parseFloat(formData.revenueGoal),
        goalEndDate: formData.goalEndDate,
        icp: formData.icp,
        website: formData.website || null,
        targetAccounts: formData.targetAccounts || null,
        currentCustomers: formData.currentCustomers || null
      });

      setProgress(100);
      clearInterval(progressInterval);
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Store userId in localStorage
      localStorage.setItem("userId", response.data.userId);
      
      toast.success("Sales machine initialized successfully!");
      
      if (onComplete) {
        onComplete(response.data.userId);
      }
      
      // Navigate to dashboard with the new userId
      navigate(`/dashboard/${response.data.userId}`);
      
    } catch (error) {
      clearInterval(progressInterval);
      console.error("Error creating user:", error);
      toast.error("Failed to initialize sales machine");
      setLoading(false);
    }
  };

  if (!isOpen) {
    console.log('[OnboardingModal] isOpen is false - returning null');
    return null;
  }
  
  console.log('[OnboardingModal] isOpen is truthy - rendering modal');

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
        <div className="bg-[#111827] border border-gray-800/50 rounded-2xl p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_pro-advisors-4/artifacts/00joaz6p_017767_Logo%20FInal%20File-01.png" 
                alt="Intelligent Sales Machines" 
                className="h-24 w-auto animate-pulse"
              />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Initializing Your Sales Machine</h3>
            <p className="text-gray-400 text-sm mb-6">Setting up your intelligent sales assistant...</p>
            
            <div className="w-full bg-gray-800 rounded-full h-2 mb-2 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-teal-500 to-teal-600 transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-xs text-gray-500">{progress}% Complete</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="bg-[#111827] border border-gray-800/50 rounded-2xl p-8 max-w-2xl w-full mx-4 relative max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Logo */}
        <div className="flex items-center justify-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_pro-advisors-4/artifacts/00joaz6p_017767_Logo%20FInal%20File-01.png" 
            alt="Intelligent Sales Machines" 
            className="h-28 w-auto"
          />
        </div>

        {/* Question */}
        <div className="mb-6">
          <p className="text-gray-300 text-lg leading-relaxed">
            {typing ? (
              <TypewriterText
                text={questions[step]}
                onComplete={() => setTyping(false)}
              />
            ) : (
              questions[step]
            )}
          </p>
        </div>

        {/* Input Fields */}
        {!typing && (
          <div className="space-y-4">
            {step === 0 && (
              <div className="text-center">
                <Button
                  onClick={handleWelcomeNext}
                  className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white px-8 py-3"
                  data-testid="onboarding-continue-btn"
                >
                  Next
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            )}
            
            {step === 1 && (
              <div className="flex items-center gap-4">
                <div className="flex-1 relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg">$</span>
                  <Input
                    type="number"
                    value={formData.revenueGoal}
                    onChange={(e) => setFormData({ ...formData, revenueGoal: e.target.value })}
                    className="w-full bg-[#0a0e17] border-gray-700 text-white pl-8 text-lg py-6 focus:border-[#3dd9c5] focus:ring-[#3dd9c5]"
                    placeholder="1000000"
                    min="1000"
                    data-testid="onboarding-revenue-input"
                  />
                </div>
                <Button
                  onClick={handleStep1Next}
                  className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white rounded-full p-4"
                  data-testid="onboarding-step1-next"
                >
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="flex-1 relative">
                    <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <Input
                      type="date"
                      value={formData.goalEndDate}
                      onChange={(e) => setFormData({ ...formData, goalEndDate: e.target.value })}
                      className="w-full bg-[#0a0e17] border-gray-700 text-white pl-12 text-lg py-6 focus:border-[#3dd9c5] focus:ring-[#3dd9c5]"
                      min={new Date().toISOString().split('T')[0]}
                      data-testid="onboarding-goaldate-input"
                    />
                  </div>
                  <Button
                    onClick={handleStep2Next}
                    className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white rounded-full p-4"
                    data-testid="onboarding-step2-next"
                  >
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </div>
                <p className="text-gray-500 text-sm text-center">
                  Setting a target date helps prioritize your sales activities and track progress
                </p>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <Input
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  className="w-full bg-[#0a0e17] border-gray-700 text-white focus:border-[#3dd9c5] focus:ring-[#3dd9c5]"
                  placeholder="Company name"
                  data-testid="onboarding-company-input"
                />
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="w-full bg-[#0a0e17] border-gray-700 text-white focus:border-[#3dd9c5] focus:ring-[#3dd9c5]"
                  placeholder="Company website (e.g., https://yourcompany.com)"
                  data-testid="onboarding-website-input"
                />
                <Textarea
                  value={formData.products}
                  onChange={(e) => setFormData({ ...formData, products: e.target.value })}
                  className="w-full bg-[#0a0e17] border-gray-700 text-white min-h-[100px] focus:border-[#3dd9c5] focus:ring-[#3dd9c5]"
                  placeholder="Describe your products or services..."
                  data-testid="onboarding-products-input"
                />
                <Button
                  onClick={handleStep3Next}
                  className="w-full bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white py-3"
                  data-testid="onboarding-step3-next"
                >
                  Continue
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-4">
                <Textarea
                  value={formData.targetAccounts}
                  onChange={(e) => setFormData({ ...formData, targetAccounts: e.target.value })}
                  className="w-full bg-[#0a0e17] border-gray-700 text-white min-h-[120px] focus:border-[#3dd9c5] focus:ring-[#3dd9c5]"
                  placeholder="e.g., Microsoft, Salesforce, HubSpot, Oracle..."
                  data-testid="onboarding-targets-input"
                />
                <div className="flex gap-3">
                  <Button
                    onClick={handleStep4Next}
                    variant="outline"
                    className="flex-1 border-gray-700 text-gray-400 hover:text-white hover:bg-white/5"
                  >
                    Skip for now
                  </Button>
                  <Button
                    onClick={handleStep4Next}
                    className="flex-1 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white"
                    data-testid="onboarding-step4-next"
                  >
                    Continue
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {step === 5 && (
              <div className="space-y-4">
                <Textarea
                  value={formData.currentCustomers}
                  onChange={(e) => setFormData({ ...formData, currentCustomers: e.target.value })}
                  className="w-full bg-[#0a0e17] border-gray-700 text-white min-h-[120px] focus:border-[#3dd9c5] focus:ring-[#3dd9c5]"
                  placeholder="e.g., Acme Corp (Fortune 500), Tech Startup Inc, MegaCo..."
                  data-testid="onboarding-customers-input"
                />
                <div className="flex gap-3">
                  <Button
                    onClick={handleStep5Next}
                    variant="outline"
                    className="flex-1 border-gray-700 text-gray-400 hover:text-white hover:bg-white/5"
                  >
                    Skip for now
                  </Button>
                  <Button
                    onClick={handleStep5Next}
                    className="flex-1 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white"
                    data-testid="onboarding-step5-next"
                  >
                    Continue
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {step === 6 && (
              <div className="space-y-4">
                <Textarea
                  value={formData.icp}
                  onChange={(e) => setFormData({ ...formData, icp: e.target.value })}
                  className="w-full bg-[#0a0e17] border-gray-700 text-white min-h-[120px] focus:border-[#3dd9c5] focus:ring-[#3dd9c5]"
                  placeholder="e.g., Mid-size B2B companies with 100-500 employees, looking to modernize their sales operations..."
                  data-testid="onboarding-icp-input"
                />
                <div className="flex gap-3">
                  <Button
                    onClick={handleSkipICP}
                    variant="outline"
                    className="flex-1 border-gray-700 text-gray-400 hover:text-white hover:bg-white/5"
                    data-testid="onboarding-skip-icp"
                  >
                    Skip - Help Me Find
                  </Button>
                  <Button
                    onClick={handleCompleteSetup}
                    className="flex-1 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white"
                    data-testid="onboarding-complete"
                  >
                    Complete Setup
                    <Sparkles className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Progress Indicator */}
        <div className="mt-8 flex justify-center gap-2">
          {[0, 1, 2, 3, 4, 5, 6].map((s) => (
            <div
              key={s}
              className={`h-2 w-8 rounded-full transition-all ${
                s === step
                  ? "bg-[#3dd9c5]"
                  : s < step
                  ? "bg-teal-700"
                  : "bg-gray-700"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
